import { useCallback, useEffect, useLayoutEffect } from 'react';

import { useLocation } from '@remix-run/react';

const STORAGE_KEY = 'positions';
const HEIGHTS_KEY = 'heights';

function getPositions() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    // If parsing fails, return an empty object
    return {};
  }
}

function getHeights() {
  try {
    return JSON.parse(sessionStorage.getItem(HEIGHTS_KEY) || '{}');
  } catch {
    return {};
  }
}

export function ScrollRestoration() {
  const location = useLocation();

  // Store the scroll position AND page height in sessionStorage
  const handler = useCallback(() => {
    const positions = getPositions();
    const heights = getHeights();
    const key = window.history.state?.key || Math.random().toString(32).slice(2);
    positions[key] = window.scrollY;
    heights[key] = document.documentElement.scrollHeight;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
    sessionStorage.setItem(HEIGHTS_KEY, JSON.stringify(heights));
  }, []);

  useEffect(() => {
    return monitorUrlChanges(handler);
  }, [handler]);

  // restore the scroll position if history matches
  // otherwise scroll to the top or to the hash
  useLayoutEffect(() => {
    if (!window.history.state || !window.history.state.key) {
      const key = Math.random().toString(32).slice(2);
      window.history.replaceState({ key }, '');
    }
    try {
      const key = window.history.state.key;
      const positions = getPositions();
      const heights = getHeights();
      const storedY = positions[key];
      const storedHeight = heights[key];

      if (typeof storedY === 'number') {
        // If we have a stored height and current page is shorter, add expansion valve
        // This prevents scroll position from being clamped before content loads
        let spacer: HTMLDivElement | null = null;
        if (storedHeight && document.documentElement.scrollHeight < storedHeight) {
          spacer = document.createElement('div');
          spacer.id = 'scroll-restoration-spacer';
          spacer.style.height = `${storedHeight - document.documentElement.scrollHeight}px`;
          spacer.style.pointerEvents = 'none';
          document.body.appendChild(spacer);
        }

        // Restore scroll position instantly (shorthand ignores CSS scroll-behavior)
        window.scrollTo(0, storedY);

        // Remove spacer after content has likely rendered (or keep it if page is still short)
        if (spacer) {
          setTimeout(() => {
            // Only remove if page has expanded beyond the spacer
            if (document.documentElement.scrollHeight >= storedHeight) {
              spacer?.remove();
            }
          }, 100);
        }

        return;
      }
    } catch {
      // If there's an error with the stored position, we just ignore it
      // and remove the key from sessionStorage
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(HEIGHTS_KEY);
    }

    // try to scroll to the hash
    try {
      if (location.hash) {
        const el = document.getElementById(decodeURIComponent(location.hash.slice(1)));
        if (el) {
          el.scrollIntoView();
          return;
        }
      }
    } catch {
      // If there's an error with the hash, we just ignore it
      // and scroll to the top
    }

    // otherwise go to the top on new locations (shorthand ignores CSS scroll-behavior)
    window.scrollTo(0, 0);
  }, [location]);

  return null;
}

function patchHistoryMethod(
  method: 'pushState' | 'replaceState',
  original: typeof history.pushState,
  onUrlChange: () => void,
): (() => void) | null {
  try {
    history[method] = function (...args) {
      onUrlChange();
      original.apply(this, args);
    };
    return () => {
      history[method] = original;
    };
  } catch (error) {
    // If monkey-patching fails (read-only property), degrade gracefully
    // We'll still capture back/forward navigation via popstate listener
    console.warn(`[ScrollRestoration] Unable to monitor ${method} - property is read-only`, error);
    return null;
  }
}

function monitorUrlChanges(onUrlChange: () => void) {
  // Save references to the original methods
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  // Try to monkey-patch history methods to detect programmatic navigation
  // In some browser contexts (privacy modes, extensions), these properties are read-only
  const cleanupPushState = patchHistoryMethod('pushState', originalPushState, onUrlChange);
  const cleanupReplaceState = patchHistoryMethod('replaceState', originalReplaceState, onUrlChange);

  return () => {
    // Restore original methods if we successfully patched them
    cleanupPushState?.();
    cleanupReplaceState?.();
  };
}
